import { useCallback, useEffect, useState } from 'react'

type UseAgentResourceOptions<T> = {
  initialData: T
  immediate?: boolean
}

type UseAgentResourceResult<T> = {
  data: T
  loading: boolean
  error: Error | null
  reload: () => Promise<void>
}

export function useAgentResource<T>(
  fetcher: () => Promise<T>,
  { initialData, immediate = true }: UseAgentResourceOptions<T>
): UseAgentResourceResult<T> {
  const [data, setData] = useState<T>(initialData)
  const [loading, setLoading] = useState<boolean>(immediate)
  const [error, setError] = useState<Error | null>(null)

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await fetcher()
      setData(result)
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load agent resource'))
      throw err
    } finally {
      setLoading(false)
    }
  }, [fetcher])

  useEffect(() => {
    if (!immediate) return

    let cancelled = false

    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const result = await fetcher()
        if (!cancelled) setData(result)
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err : new Error('Failed to load agent resource'))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()

    return () => {
      cancelled = true
    }
  }, [fetcher, immediate])

  return { data, loading, error, reload }
}
